import { Options } from 'highcharts';
import { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { IBatteryInputData } from './IBatteryInputData.ts';
import { IRunMetrics } from './IRunMetrics.ts';
import { IIdNameWithBooleanInfo } from './IIdNameWithBooleanInfo.ts';
import { IIdName } from '../general/IIdName.ts';

type actionType = 'add' | 'edit' | 'clone';
type chartType = 'mean' | 'min' | 'p25' | 'p75'| 'max' | 'p10' | 'p50' | 'p90';
type periodType = 'calendarYearly' | 'financialYearly' | 'quarterly' | 'monthly';

interface IChartSeriesDataYear {
    [key: number]: number[];
}

interface IChartSeriesData {
    data: number[];
    dataByYear?: IChartSeriesDataYear;
    color?: string | null;
    name: string;
}

interface IChartSeries {
    data: Array<IChartSeriesData>;
    pointInterval: number;
    pointIntervalUnit: string;
    pointStart: Date;
    pointEnd: Date;
    unit: string;
    metricByYear: Record<number, IOutputMetric>;
}

interface IOutputChartDto extends Omit<IChartSeries, 'pointStart' | 'pointEnd'> {
    pointStart: string;
    pointEnd: string;
}

export interface IOutputChartResponse {
    chart: IOutputChartDto;
    type: string;
}

export interface IMarketChartResponse {
    chart: IOutputChartDto;
    type: string;
}

interface IOutputChartListItem {
    key: string;
    name: string;
    category: string;
}

interface IOutputMetric {
    name: string;
    prefix: string;
    suffix: string;
    value: number;
}

interface IOptionWithMetric {
    metric: IOutputMetric;
    description: string[];
    chart: IRunOutputCharts;
    yAxis: {
        min: number;
        max: number;
    };
}

interface IRunOutputCharts {
    values: Record<chartType, Record<periodType, IChartSeries>>;
    description: string[];
    metric: IOutputMetric;
    name: string;
    type: string;
}

interface IOverviewChartPeriod {
    data: Array<IChartSeriesData>;
    pointInterval: number;
    pointIntervalUnit: string;
    pointStart: string;
    pointEnd: string;
    unit: string;
}

type IOverviewChartByPercentile = Record<chartType, Record<periodType, IOverviewChartPeriod>>;

type IOverviewChartDto = Array<IOverviewChartByPercentile>;

interface IRunOutputCategory {
    charts: Record<string, IRunOutputCharts>;
    inputs?: IBatteryInputData;
    name: string;
    showAssetDetails: boolean;
}

interface IRunOutputDto {
    data: {
        view: Record<string, IRunOutputCategory>;
        years: Array<number>;
        summaryMetrics: IRunMetrics;
    };
    country: TStorageForecastCountries;
    id: string;
    inputId: IIdNameWithBooleanInfo;
    runId: string;
    resultAccess: ResultAccess;
}

export enum ResultAccess {
    LcpDefault = 'LcpDefault',
    User = 'User',
    Admin = 'Admin',
    Company = 'Company',
    Unknown = 'Unknown',
    NoAccess = 'NoAccess',
}

export interface IMarketChartsPayload {
    assumptionsId: string;
    marketChartCategory: MarketChartsCategories;
}

export interface IMarketChartsListRequest {
    assumptionsId: string;
    marketChartCategory: MarketChartsCategories;
}

export interface IMarketChartRequest {
    assumptionsId: string;
    chartName: string;
    chartCategory: MarketChartsCategories;
    startDate?: string;
    endDate?: string;
}

export enum MarketChartsCategories {
    SystemAssumptions = 'SystemAssumptions',
    SystemForecasts = 'SystemForecasts',
    CapacityMarket = 'CapacityMarket',
    TNUoS = 'TNUoS',
    TLMs = 'TLMs',
}

interface IMarketCharts {
    data: {
        charts: Array<IRunOutputCharts>;
        years: Array<number>;
    };
    id: string;
}

export interface IQuarterScenarioPair {
    quarter: IIdName | null;
    scenario: IIdName| null;
}

interface ISelectedChartOptions {
    selectedAssetsWithChart: Array<IAssetWithChart>;
    comparisonAssetsWithChart: Array<IAssetWithChart>;
}

interface IAssetWithChart {
    chart: Options;
    description: string[];
    yAxis: {
        min: number;
        max: number;
    };
    metricByYear: Record<number, IOutputMetric>;
}

export type {
    ISelectedChartOptions,
    IAssetWithChart,
    IChartSeries,
    IOutputChartDto,
    IOutputChartListItem,
    IChartSeriesData,
    IRunOutputCategory,
    IRunOutputCharts,
    IRunOutputDto,
    IOverviewChartPeriod,
    IOverviewChartByPercentile,
    IOverviewChartDto,
    actionType,
    chartType,
    periodType,
    IOptionWithMetric,
    IOutputMetric,
    IMarketCharts,
};
